import React from 'react';
import { Card, Col, Empty, Progress, Row, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, Line } from '@ant-design/plots';
import type { ColumnsType } from 'antd/es/table';

const { Paragraph, Text } = Typography;

interface TrendRequestAnalysisProps {
  data: any;
  type: string;
}

const number = (value: unknown): number => Number(value || 0);
const formatNumber = (value: unknown): string => number(value).toLocaleString();

const ErrorRate: React.FC<{ value: unknown }> = ({ value }) => {
  const rate = number(value);
  return (
    <Progress
      percent={rate}
      size="small"
      status={rate > 5 ? 'exception' : 'normal'}
      format={() => `${rate.toFixed(1)}%`}
    />
  );
};

const TrendRequestAnalysis: React.FC<TrendRequestAnalysisProps> = ({ data, type }) => {
  const rows = Array.isArray(data?.data)
    ? data.data.map((item: any, index: number) => ({ ...item, key: `${type}-${index}` }))
    : [];

  if (!data || !Array.isArray(data.data)) {
    return <Empty description="無可用資料" />;
  }

  const timeRange = data.time_range ? (
    <Text type="secondary">
      查詢時間範圍：{data.time_range.start_date} ～ {data.time_range.end_date}
    </Text>
  ) : null;

  if (type === 'trend_hotposts_summary') {
    const totalRequests = rows.reduce((sum: number, row: any) => sum + number(row.total_requests), 0);
    const customPromptRequests = rows.reduce(
      (sum: number, row: any) => sum + number(row.custom_prompt_requests),
      0,
    );
    const organizationCount = new Set(rows.map((row: any) => row.org_name)).size;
    const activeDays = new Set(rows.map((row: any) => row.event_date)).size;
    const customPromptRate = totalRequests > 0
      ? (customPromptRequests / totalRequests) * 100
      : 0;

    const dailyMap = new Map<string, { requests: number; organizations: Set<string> }>();
    rows.forEach((row: any) => {
      const current = dailyMap.get(row.event_date) || { requests: 0, organizations: new Set<string>() };
      current.requests += number(row.total_requests);
      current.organizations.add(row.org_name);
      dailyMap.set(row.event_date, current);
    });
    const dailyChartData = Array.from(dailyMap.entries())
      .flatMap(([date, value]) => [
        { date, value: value.requests, type: '摘要請求' },
        { date, value: value.organizations.size, type: '活躍組織' },
      ])
      .sort((a, b) => a.date.localeCompare(b.date));

    const organizationMap = new Map<string, {
      requests: number;
      customPrompts: number;
      days: Set<string>;
      modes: Set<string>;
      channels: Set<string>;
      lastUsage: string;
    }>();
    rows.forEach((row: any) => {
      const current = organizationMap.get(row.org_name) || {
        requests: 0,
        customPrompts: 0,
        days: new Set<string>(),
        modes: new Set<string>(),
        channels: new Set<string>(),
        lastUsage: '',
      };
      current.requests += number(row.total_requests);
      current.customPrompts += number(row.custom_prompt_requests);
      current.days.add(row.event_date);
      current.modes.add(row.mode);
      current.channels.add(row.channel);
      current.lastUsage = current.lastUsage > row.event_date ? current.lastUsage : row.event_date;
      organizationMap.set(row.org_name, current);
    });
    const organizationRows = Array.from(organizationMap.entries())
      .map(([orgName, value]) => ({
        key: orgName,
        org_name: orgName,
        requests: value.requests,
        custom_prompts: value.customPrompts,
        active_days: value.days.size,
        mode_count: value.modes.size,
        channel_count: value.channels.size,
        last_usage: value.lastUsage,
      }))
      .sort((a, b) => b.requests - a.requests);

    const modeLabels: Record<string, string> = {
      custom: '自訂摘要',
      summary_essence: '重點摘要',
      keyword_overview: '關鍵詞總覽',
      topic_focus: '主題聚焦',
    };
    const dimensionRows = (field: 'mode' | 'channel' | 'sort_item') => {
      const values = new Map<string, { requests: number; organizations: Set<string> }>();
      rows.forEach((row: any) => {
        const key = row[field] || '未指定';
        const current = values.get(key) || { requests: 0, organizations: new Set<string>() };
        current.requests += number(row.total_requests);
        current.organizations.add(row.org_name);
        values.set(key, current);
      });
      return Array.from(values.entries())
        .map(([value, aggregate]) => ({
          key: `${field}-${value}`,
          value: field === 'mode' ? (modeLabels[value] || value) : value,
          requests: aggregate.requests,
          organizations: aggregate.organizations.size,
          share: totalRequests > 0 ? (aggregate.requests / totalRequests) * 100 : 0,
        }))
        .sort((a, b) => b.requests - a.requests);
    };
    const sentimentRows = [
      ['正向', 'positive_requests'],
      ['中立', 'neutral_requests'],
      ['負向', 'negative_requests'],
    ].map(([label, field]) => {
      const requests = rows.reduce((sum: number, row: any) => sum + number(row[field]), 0);
      return {
        key: field,
        value: label,
        requests,
        organizations: new Set(
          rows.filter((row: any) => number(row[field]) > 0).map((row: any) => row.org_name),
        ).size,
        share: totalRequests > 0 ? (requests / totalRequests) * 100 : 0,
      };
    });

    const dimensionColumns: ColumnsType<any> = [
      { title: '項目', dataIndex: 'value', render: (value) => <Tag color="purple">{value}</Tag> },
      { title: '請求量', dataIndex: 'requests', render: formatNumber },
      { title: '採用組織', dataIndex: 'organizations' },
      {
        title: '使用占比',
        dataIndex: 'share',
        width: 170,
        render: (value) => <Progress percent={number(value)} size="small" format={(percent) => `${number(percent).toFixed(1)}%`} />,
      },
    ];
    const organizationColumns: ColumnsType<any> = [
      { title: '組織', dataIndex: 'org_name', fixed: 'left', width: 240 },
      { title: '摘要請求', dataIndex: 'requests', sorter: (a, b) => a.requests - b.requests, render: formatNumber },
      { title: '自訂 Prompt', dataIndex: 'custom_prompts', render: formatNumber },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '採用模式', dataIndex: 'mode_count' },
      { title: '使用頻道', dataIndex: 'channel_count' },
      { title: '最後使用', dataIndex: 'last_usage' },
    ];

    return (
      <>
        <Paragraph>
          「熱門文章摘要」產品的請求採用分析。來源只記錄收到請求，因此不將請求量解讀為成功完成量。
        </Paragraph>
        {timeRange}
        <Row gutter={[16, 16]} style={{ margin: '16px 0' }}>
          <Col xs={24} md={6}><Card><Statistic title="摘要請求" value={totalRequests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="使用組織" value={organizationCount} /></Card></Col>
          <Col xs={24} md={6}>
            <Card><Statistic title="自訂 Prompt" value={customPromptRequests} suffix={`(${customPromptRate.toFixed(1)}%)`} /></Card>
          </Col>
          <Col xs={24} md={6}><Card><Statistic title="活躍天數" value={activeDays} /></Card></Col>
        </Row>
        <Card title="每日使用趨勢" style={{ marginBottom: 16 }}>
          {dailyChartData.length > 0 ? (
            <Line data={dailyChartData} xField="date" yField="value" colorField="type" height={340} />
          ) : <Empty />}
        </Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} xl={12}>
            <Card title="摘要模式">
              <Table columns={dimensionColumns} dataSource={dimensionRows('mode')} pagination={false} size="small" />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="來源頻道">
              <Table columns={dimensionColumns} dataSource={dimensionRows('channel')} pagination={false} size="small" />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="排序方式">
              <Table columns={dimensionColumns} dataSource={dimensionRows('sort_item')} pagination={{ pageSize: 8 }} size="small" />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="情緒篩選選擇">
              <Paragraph type="secondary">同一次請求可同時選擇多種情緒，因此占比合計可能超過 100%。</Paragraph>
              <Table columns={dimensionColumns} dataSource={sentimentRows} pagination={false} size="small" />
            </Card>
          </Col>
        </Row>
        <Card title="組織採用明細">
          <Table columns={organizationColumns} dataSource={organizationRows} scroll={{ x: 1050 }} pagination={{ pageSize: 20 }} />
        </Card>
      </>
    );
  }

  if (type === 'trend_overview') {
    const totalRequests = rows.reduce((sum: number, row: any) => sum + number(row.total_requests), 0);
    const organizationCount = new Set(rows.map((row: any) => row.org_name)).size;
    const activeUsers = rows.reduce((sum: number, row: any) => sum + number(row.active_users), 0);
    const errors = rows.reduce((sum: number, row: any) => sum + number(row.error_requests), 0);

    const columns: ColumnsType<any> = [
      { title: '組織', dataIndex: 'org_name', fixed: 'left', width: 220 },
      { title: '有效操作', dataIndex: 'total_requests', sorter: (a, b) => number(a.total_requests) - number(b.total_requests), render: formatNumber },
      { title: '活躍使用者', dataIndex: 'active_users' },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '採用功能數', dataIndex: 'adopted_features' },
      { title: 'Segment', dataIndex: 'segment_requests', render: formatNumber },
      { title: 'Alert', dataIndex: 'alert_requests', render: formatNumber },
      { title: 'Export', dataIndex: 'export_requests', render: formatNumber },
      { title: '錯誤率', dataIndex: 'error_rate', width: 160, render: (value) => <ErrorRate value={value} /> },
      { title: '平均延遲', dataIndex: 'avg_latency', render: (value) => `${number(value).toFixed(3)} 秒` },
      { title: '最後使用', dataIndex: 'last_usage_date', width: 120 },
    ];

    return (
      <>
        <Paragraph>僅統計可辨識的產品操作，常見頁面初始化與背景載入請求已排除。</Paragraph>
        {timeRange}
        <Row gutter={16} style={{ margin: '16px 0' }}>
          <Col xs={24} md={6}><Card><Statistic title="使用組織" value={organizationCount} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="有效操作" value={totalRequests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="活躍使用者（組織加總）" value={activeUsers} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="錯誤請求" value={errors} /></Card></Col>
        </Row>
        <Card>
          <Table columns={columns} dataSource={rows} scroll={{ x: 1450 }} pagination={{ pageSize: 20 }} />
        </Card>
      </>
    );
  }

  if (type === 'trend_features') {
    const featureLabels: Record<string, string> = {
      Search: '搜尋',
      Segment: '客群管理',
      Alert: '警示',
      Export: '資料匯出',
      'Saved Words': '儲存關鍵詞',
      Report: '報告',
      Trending: '熱門／趨勢',
      Source: '資料來源',
      Preference: '偏好設定',
      Other: '其他',
    };
    const totalActions = rows.reduce(
      (sum: number, row: any) => sum + number(row.total_requests),
      0,
    );
    const totalPaths = rows.reduce(
      (sum: number, row: any) => sum + number(row.path_count),
      0,
    );
    const mostUsedFeature = [...rows].sort(
      (a: any, b: any) => number(b.total_requests) - number(a.total_requests),
    )[0];
    const chartData = rows
      .map((row: any) => ({
        feature: featureLabels[row.feature] || row.feature,
        actions: number(row.total_requests),
      }))
      .sort((a: any, b: any) => b.actions - a.actions);

    const columns: ColumnsType<any> = [
      {
        title: '功能類別',
        dataIndex: 'feature',
        fixed: 'left',
        width: 160,
        render: (value) => <Tag color="purple">{featureLabels[value] || value}</Tag>,
      },
      { title: '有效操作', dataIndex: 'total_requests', sorter: (a, b) => number(a.total_requests) - number(b.total_requests), render: formatNumber },
      {
        title: '使用占比',
        width: 170,
        render: (_, row) => {
          const share = totalActions > 0
            ? (number(row.total_requests) / totalActions) * 100
            : 0;
          return <Progress percent={share} size="small" format={() => `${share.toFixed(1)}%`} />;
        },
      },
      { title: '採用組織', dataIndex: 'org_count' },
      { title: '使用者', dataIndex: 'user_count' },
      { title: '相關路徑', dataIndex: 'path_count' },
      { title: '錯誤率', dataIndex: 'error_rate', width: 160, render: (value) => <ErrorRate value={value} /> },
      { title: '平均延遲', dataIndex: 'avg_latency', render: (value) => `${number(value).toFixed(3)} 秒` },
      { title: 'P95 延遲', dataIndex: 'p95_latency', render: (value) => `${number(value).toFixed(3)} 秒` },
    ];
    const expandedRowRender = (row: any) => {
      const pathRows = (Array.isArray(row.paths) ? row.paths : []).map(
        (path: string, index: number) => ({
          key: `${row.feature}-${path}`,
          path,
          requests: Array.isArray(row.path_request_counts)
            ? row.path_request_counts[index]
            : 0,
        }),
      );
      return (
        <Table
          columns={[
            { title: 'Endpoint 路徑', dataIndex: 'path' },
            { title: '有效操作', dataIndex: 'requests', width: 160, render: formatNumber },
          ]}
          dataSource={pathRows}
          pagination={false}
          size="small"
        />
      );
    };
    return (
      <>
        <Paragraph>查看 Segment、Alert、Export、Saved Words、Report、Trending 等實際功能操作。</Paragraph>
        {timeRange}
        <Row gutter={[16, 16]} style={{ margin: '16px 0' }}>
          <Col xs={24} md={8}><Card><Statistic title="功能類別" value={rows.length} /></Card></Col>
          <Col xs={24} md={8}><Card><Statistic title="有效操作" value={totalActions} /></Card></Col>
          <Col xs={24} md={8}><Card><Statistic title="實際 Endpoint" value={totalPaths} /></Card></Col>
        </Row>
        <Card title="功能採用分布" style={{ marginBottom: 16 }}>
          {chartData.length > 0 ? (
            <Bar data={chartData} xField="actions" yField="feature" height={360} />
          ) : <Empty />}
        </Card>
        <Card title={`功能類別明細${mostUsedFeature ? `｜最多使用：${featureLabels[mostUsedFeature.feature] || mostUsedFeature.feature}` : ''}`}>
          <Table
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1250 }}
            pagination={false}
            expandable={{ expandedRowRender }}
          />
        </Card>
      </>
    );
  }

  if (type === 'trend_activity') {
    const daily = new Map<string, { date: string; actions: number; users: number }>();
    rows.forEach((row: any) => {
      const current = daily.get(row.event_date) || { date: row.event_date, actions: 0, users: 0 };
      current.actions += number(row.total_requests);
      current.users += number(row.active_users);
      daily.set(row.event_date, current);
    });
    const chartData = Array.from(daily.values())
      .flatMap((item) => [
        { date: item.date, value: item.actions, type: '有效操作' },
        { date: item.date, value: item.users, type: '活躍使用者（組織加總）' },
      ])
      .sort((a, b) => a.date.localeCompare(b.date));

    const columns: ColumnsType<any> = [
      { title: '日期', dataIndex: 'event_date', width: 120 },
      { title: '組織', dataIndex: 'org_name', width: 220 },
      { title: '有效操作', dataIndex: 'total_requests', sorter: (a, b) => number(a.total_requests) - number(b.total_requests), render: formatNumber },
      { title: '活躍使用者', dataIndex: 'active_users' },
      { title: '錯誤請求', dataIndex: 'error_requests', render: formatNumber },
    ];

    return (
      <>
        <Card title="每日產品使用趨勢" style={{ marginBottom: 16 }}>
          {timeRange}
          {chartData.length > 0 ? (
            <Line
              data={chartData}
              xField="date"
              yField="value"
              colorField="type"
              height={360}
              style={{ marginTop: 16 }}
            />
          ) : <Empty />}
        </Card>
        <Card title="每日組織明細">
          <Table columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} />
        </Card>
      </>
    );
  }

  return <Empty description="未知的 Trend 產品行為分析類型" />;
};

export default TrendRequestAnalysis;
